import React, { useReducer, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../../../components/Common/UI/Dialog';
import Icons from '../../../../../components/Common/Icons/Icons';
import { Input } from '../../../../../components/Common/UI/Input';
import StateSelect from '../../../../../components/Common/StateSelect';
import { Button } from '../../../../../components/Common/UI/Button';
import { infoTracUser } from '../../../../../contstant';
import Separator from '../../../../../components/Common/UI/Separator';

interface IState {
  username: string;
  firstName: string;
  lastName: string;
  title: string;
  company: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  // ContactInformation
  website: string;
  officePhone: string;
  ext: string;
  fax: string;
  email: string;
  roles: string;
  manageAlerts: string;
  // Password
  password: string;
}

const reducerDefaultValue: IState = {
  username: infoTracUser.username,
  firstName: infoTracUser.contactInformation.firstName,
  lastName: infoTracUser.contactInformation.lastName,
  title: infoTracUser.title,
  company: infoTracUser.company,
  address: infoTracUser.address.name,
  city: infoTracUser.address.city,
  state: infoTracUser.address.state,
  zip: infoTracUser.address.zip,
  // ContactInformation
  website: infoTracUser.contactInformation.website,
  officePhone: infoTracUser.contactInformation.officePhone,
  ext: infoTracUser.contactInformation.ext,
  fax: infoTracUser.contactInformation.fax,
  email: infoTracUser.email,
  roles: infoTracUser.manageSettings.roles,
  manageAlerts: infoTracUser.manageSettings.manageAlerts,
  // Password
  password: infoTracUser.passwordPlaceholder,
};

type ShowPasswordType = 'pass' | 'newPass' | 'confirmPass';

function ContainerTitle({ children }: React.PropsWithChildren) {
  return <p className="text-addition1 mb-4 font-bold">{children}</p>;
}

function UpdateProfileDialog() {
  const [modalOpen, setModalOpen] = useState(false);
  const [isChangingPass, setIsChangingPass] = useState(false);
  const [showPassword, setShowPassword] = useState({
    pass: false,
    newPass: false,
    confirmPass: false,
  });

  const handleToggleShowPass = (type: ShowPasswordType) => {
    setShowPassword(prevState => ({
      ...prevState,
      [type]: !prevState[type],
    }));
  };

  const reducer = (prevState: IState, action: Partial<IState>) => ({ ...prevState, ...action });
  const [state, dispatch] = useReducer(reducer, reducerDefaultValue);
  // const handleDispatch = (payload: Partial<IState>) => {
  //   dispatch(payload);
  // };

  return (
    <Dialog open={modalOpen} onOpenChange={isOpen => setModalOpen(isOpen)}>
      <DialogTrigger variant="filled">
        <Icons.PencilEdit className="size-[20px]" />
      </DialogTrigger>
      <DialogContent className="overflow-y-scroll max-h-[98%]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-4">
            <div className="bg-addition5 p-1 rounded-lg">
              <Icons.PencilEdit className="size-[24px] text-white" />
            </div>
            <div>
              <p className="text-xl">My Profile</p>
              <p className="text-muted-foreground font-normal text-base">Some text description</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 pt-4">
          <div className="content-group">
            <ContainerTitle>Login information</ContainerTitle>
            <Input label="Current username" placeholder="Type here..." defaultValue={state.username} />

            {isChangingPass ? (
              <div className="grid grid-cols-2 mt-2">
                <div className="col-start-2 flex flex-col gap-4">
                  <Input
                    label="Current Password"
                    placeholder="Type here..."
                    type={showPassword.pass ? 'text' : 'password'}
                    defaultValue={state.password}
                    icon={
                      // if confirmPass false then eye should be closed. If we will have new icon from DS team - add it
                      <Button size="auto" variant="ghost" onClick={() => handleToggleShowPass('pass')}>
                        {showPassword.confirmPass ? <Icons.EyeFilled /> : <Icons.EyeFilled />}
                      </Button>
                    }
                  />
                  <Input
                    label="New Password"
                    placeholder="Type here..."
                    type={showPassword.newPass ? 'text' : 'password'}
                    defaultValue=""
                    icon={
                      <Button size="auto" variant="ghost" onClick={() => handleToggleShowPass('newPass')}>
                        {showPassword.confirmPass ? <Icons.EyeFilled /> : <Icons.EyeFilled />}
                      </Button>
                    }
                  />
                  <Input
                    label="Confirm New Password"
                    placeholder="Type here..."
                    type={showPassword.confirmPass ? 'text' : 'password'}
                    defaultValue=""
                    icon={
                      <Button size="auto" variant="ghost" onClick={() => handleToggleShowPass('confirmPass')}>
                        {showPassword.confirmPass ? <Icons.EyeFilled /> : <Icons.EyeFilled />}
                      </Button>
                    }
                  />
                  <div className="flex items-center gap-4 justify-center">
                    <Button variant="link" className="py-3" onClick={() => setIsChangingPass(false)}>
                      Cancel
                    </Button>
                    <Button variant="default" className="py-3" onClick={() => setIsChangingPass(false)}>
                      Save Password
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-end">
                <Button size="auto" variant="link" className="py-3" onClick={() => setIsChangingPass(true)}>
                  <Icons.TextEditOutlined /> Change Password
                </Button>
              </div>
            )}
          </div>

          <div className="content-group">
            <ContainerTitle>Personal Information</ContainerTitle>

            <div className="grid grid-cols-2 gap-4">
              <Input label="First name" placeholder="Type here..." name="firstName" defaultValue={state.firstName} />
              <Input label="Last name" placeholder="Type here..." defaultValue={state.lastName} />

              <Input label="Title" placeholder="Type here..." defaultValue={state.title} />
              <Input label="Company" placeholder="Type here..." defaultValue={state.company} />

              <Input label="Address" placeholder="Type here..." defaultValue={state.address} />
              <Input label="City" placeholder="Type here..." defaultValue={state.city} />

              <div className="grid grid-cols-2 gap-4">
                <StateSelect label="State" defaultValue={state.state} />
                <Input label="Zip" placeholder="Type here..." defaultValue={state.zip} />
              </div>
            </div>
          </div>

          <div className="content-group">
            <ContainerTitle>Contact Information</ContainerTitle>

            <div className="grid grid-cols-2 gap-4">
              <Input label="Website" placeholder="Type here..." defaultValue={state.website} />
              <Input label="Office Phone" placeholder="Type here..." defaultValue={state.officePhone} />

              <div className="grid grid-cols-2 gap-4">
                <Input label="Ext" placeholder="Type here..." defaultValue={state.ext} />
                <Input label="Fax" placeholder="Type here..." defaultValue={state.fax} />
              </div>
              <Input label="Email" placeholder="Type here..." defaultValue={state.email} />
            </div>
          </div>

          <div className="content-group">
            <ContainerTitle>Manage settings</ContainerTitle>

            <div className="grid grid-cols-2 gap-4">
              <Input label="Roles" placeholder="Type here..." defaultValue={state.roles} />
              <Input label="Manage Alerts" placeholder="Type here..." defaultValue={state.manageAlerts} />
            </div>
          </div>
        </div>

        <Separator className="!mt-4" />

        <div className="flex justify-end gap-4">
          <Button variant="link" className="py-3" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="default" className="py-3" onClick={() => setModalOpen(false)}>
            <Icons.SaveFilled />
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UpdateProfileDialog;
